import React from 'react';
import { Search, Filter, Grid as ViewGrid, List as ViewList, SortAsc as SortAscending } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type Props = {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  countryFilter: string;
  setCountryFilter: (value: string) => void;
  countries: string[];
  viewMode: 'list' | 'cards';
  setViewMode: (mode: 'list' | 'cards') => void;
  sortField: 'name' | 'createdAt' | 'country';
  sortDirection: 'asc' | 'desc';
  onSort: (field: 'name' | 'createdAt' | 'country') => void;
  getSortIcon: (field: 'name' | 'createdAt' | 'country') => React.ReactNode;
};

export const OrganizerFilters = ({
  searchTerm,
  setSearchTerm,
  countryFilter,
  setCountryFilter,
  countries,
  viewMode,
  setViewMode,
  sortField,
  sortDirection,
  onSort,
  getSortIcon
}: Props) => {
  const { translations } = useLanguage();

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
        {/* Search - Takes 6 columns on medium screens */}
        <div className="md:col-span-6 relative">
          <input
            type="text"
            placeholder={translations?.organizerFilters?.searchPlaceholder || 'Search by name or email...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Country Filter - Takes 3 columns on medium screens */}
        <div className="md:col-span-3">
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">
              {translations?.organizerFilters?.allCountries || 'All Countries'}
            </option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        {/* Sort and View Controls - Take 3 columns on medium screens */}
        <div className="md:col-span-3 flex justify-end gap-2">
          {/* Sort Dropdown */}
          <div className="relative group">
            <button className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
              <SortAscending size={20} className="text-gray-600" />
            </button>
            <div className="absolute right-0 mt-1 w-48 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-1">
                <button
                  onClick={() => onSort('name')}
                  className="px-4 py-2 w-full text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{translations?.organizerFilters?.sortByName || 'Name'}</span>
                  {getSortIcon('name')}
                </button>
                <button
                  onClick={() => onSort('country')}
                  className="px-4 py-2 w-full text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{translations?.organizerFilters?.sortByCountry || 'Country'}</span>
                  {getSortIcon('country')}
                </button>
                <button
                  onClick={() => onSort('createdAt')}
                  className="px-4 py-2 w-full text-left text-sm hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{translations?.organizerFilters?.sortByDate || 'Date'}</span>
                  {getSortIcon('createdAt')}
                </button>
              </div>
            </div>
          </div>

          {/* View Toggle */}
          <div className="bg-gray-50 rounded-lg p-1 flex">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-1 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500'}`}
            >
              <ViewGrid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500'}`}
            >
              <ViewList size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {(searchTerm || countryFilter) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="flex items-center gap-2"
        >
          <Filter size={16} className="text-primary-500" />
          <span className="text-sm text-gray-600">
            {translations?.organizerFilters?.activeFilters || 'Active Filters:'}
          </span>
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {translations?.organizerFilters?.search || 'Search'}: {searchTerm}
              </span>
            )}
            {countryFilter && (
              <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {translations?.organizerFilters?.country || 'Country'}: {countryFilter}
              </span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};